import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getGift } from '../../services/giftService';
import styles from './GiftRedeemPage.module.css';

// Luxury gift-membership unboxing / redeem page (public, no auth).
const GiftRedeemPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [gift, setGift] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { gift: data } = await getGift(code);
        if (active) setGift(data);
      } catch (error) {
        // A 404 is the only answer that means this code is not a gift. Every other
        // failure is ours, and telling a recipient their paid gift is invalid would
        // be a lie that sends them back to the gifter. Same split as
        // `user/ProductsPage.jsx` (card 28286) and `BlogPostPage` (card 53607).
        if (active) {
          if (error?.status === 404) setInvalid(true);
          else setLoadError(true);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [code, reloadToken]);

  const handleOpen = useCallback(() => setOpened(true), []);
  const handleRetry = useCallback(() => {
    setLoading(true);
    setInvalid(false);
    setLoadError(false);
    setReloadToken((token) => token + 1);
  }, []);
  const handleClaim = useCallback(
    () => navigate(`/gift/${code}/create-account`),
    [navigate, code]
  );

  const renderStateMessage = (title, subtitle) => (
    <div className={styles.stateCard}>
      <div className={styles.stateMark} />
      <h1 className={styles.stateTitle}>{title}</h1>
      <p className={styles.stateSubtitle}>{subtitle}</p>
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.glow} />
      <div className={styles.content}>
        {loading && (
          <div className={styles.stateCard}>
            <div className={styles.spinner} />
            <p className={styles.stateSubtitle}>Preparing your gift…</p>
          </div>
        )}

        {!loading && loadError && (
          <div className={styles.stateCard}>
            <div className={styles.stateMark} />
            <h1 className={styles.stateTitle}>We couldn&rsquo;t load your gift</h1>
            <p className={styles.stateSubtitle}>
              Something went wrong on our end &mdash; your gift is safe. Please try again.
            </p>
            <button className={styles.cta} onClick={handleRetry} type="button">
              Try again
            </button>
          </div>
        )}

        {!loading && !loadError && invalid &&
          renderStateMessage(
            'This gift link is no longer valid',
            'The link may be incorrect or has expired. Please check with the person who shared it.'
          )}

        {!loading && !invalid && gift?.status === 'redeemed' &&
          renderStateMessage(
            'This gift has already been claimed',
            'This complimentary membership has been redeemed and is no longer available.'
          )}

        {!loading && !invalid && gift?.status === 'void' &&
          renderStateMessage(
            'This gift link is no longer valid',
            'This gift was withdrawn and can no longer be claimed.'
          )}

        {!loading && !invalid && gift?.status === 'active' && (
          <div className={styles.unboxing}>
            <p className={styles.eyebrow}>A gift, just for you</p>

            <div
              className={`${styles.box} ${opened ? styles.boxOpened : ''}`}
              role="button"
              tabIndex={0}
              onClick={!opened ? handleOpen : undefined}
              onKeyDown={(e) => {
                if (!opened && (e.key === 'Enter' || e.key === ' ')) handleOpen();
              }}
              aria-label="Open your gift"
            >
              <div className={styles.boxGlow} />
              <div className={styles.lid}>
                <div className={styles.ribbonV} />
                <div className={styles.bow} />
              </div>
              <div className={styles.base}>
                <div className={styles.ribbonVBase} />
              </div>

              <div className={styles.reveal}>
                <span className={styles.revealLabel}>Complimentary Membership</span>
                <span className={styles.revealTier}>{gift.tierName}</span>
                <span className={styles.revealDuration}>
                  {gift.durationMonths} {gift.durationMonths === 1 ? 'month' : 'months'}
                </span>
              </div>
            </div>

            {!opened ? (
              <>
                <h1 className={styles.title}>Open your gift</h1>
                <p className={styles.subtitle}>
                  Someone has gifted you a complimentary Oval membership. Tap the box to reveal it.
                </p>
                <button className={styles.cta} onClick={handleOpen} type="button">
                  Open your gift
                </button>
              </>
            ) : (
              <>
                <h1 className={styles.title}>
                  A complimentary {gift.tierName} membership
                </h1>
                <p className={styles.subtitle}>
                  {gift.durationMonths} {gift.durationMonths === 1 ? 'month' : 'months'} of Oval,
                  on the house. Create your account to claim it.
                </p>
                <button className={styles.cta} onClick={handleClaim} type="button">
                  Claim your gift
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftRedeemPage;
